import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Copy, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PROFILE_KEY = 'saanjh_customer';

const loadProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && p.name && p.phone ? p : null;
  } catch {
    return null;
  }
};

const to12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const CheckoutDialog = ({ open, onOpenChange, cart, vendorSlug, totalPrice, currencySymbol = '₹', vendor }) => {
  const { clearCart } = useCart();
  const [step, setStep] = useState('info'); // 'info' | 'confirm' | 'payment'
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState({ mode: 'asap', time: '' });
  const [paymentMethod, setPaymentMethod] = useState('counter'); // 'online' | 'counter'
  const [upiCopied, setUpiCopied] = useState(false);

  const hasQrOrUpi = !!(vendor?.upi_id || vendor?.payment_qr_url);

  useEffect(() => {
    if (!open) return;
    const profile = loadProfile();
    if (profile) {
      setFormData({ name: profile.name, phone: profile.phone });
      setStep('confirm');
    } else {
      setFormData({ name: '', phone: '' });
      setStep('info');
    }
    setSchedule({ mode: 'asap', time: '' });
    setPaymentMethod('counter');
    setUpiCopied(false);
  }, [open]);

  const validateName = () => {
    if (!(formData.name || '').trim()) { toast.error('Please enter your name.'); return false; }
    return true;
  };

  const validatePhone = () => {
    const digits = (formData.phone || '').replace(/\D/g, '');
    if (digits.length < 10) { toast.error('Please enter a valid WhatsApp number.'); return false; }
    return true;
  };

  const validateSchedule = () => {
    if (schedule.mode === 'scheduled' && !schedule.time) {
      toast.error('Please choose a time, or select As soon as possible.');
      return false;
    }
    return true;
  };

  const proceedFromInfo = () => {
    if (!validateName() || !validatePhone() || !validateSchedule()) return;
    setStep('payment');
  };

  const proceedFromConfirm = () => {
    if (!validateSchedule()) return;
    setStep('payment');
  };

  const placeOrder = async (method) => {
    const pm = method || paymentMethod;
    setLoading(true);
    try {
      const items = cart.map((item) => ({ product_id: item.product.id, quantity: item.quantity }));
      const scheduled = schedule.mode === 'scheduled' && schedule.time;
      const res = await axios.post(`${API}/checkout/place-order`, {
        vendor_slug: vendorSlug,
        items,
        customer_name: formData.name.trim(),
        customer_phone: formData.phone,
        fulfilment: scheduled ? 'scheduled' : 'asap',
        scheduled_for: scheduled ? schedule.time : null,
        scheduled_label: scheduled ? `Today, ${to12h(schedule.time)}` : null,
        payment_method: pm,
      }, { timeout: 20000 });
      localStorage.setItem(PROFILE_KEY, JSON.stringify({ name: formData.name.trim(), phone: formData.phone }));
      clearCart();
      toast.success('Order placed successfully! 🎉', {
        description: 'The store has your order and will confirm shortly on WhatsApp.',
        duration: 5000,
      });
      onOpenChange(false);
      const params = new URLSearchParams({ order_id: res.data.order_id, vendor_slug: vendorSlug });
      if (pm === 'online') params.set('payment_method', 'online');
      setTimeout(() => { window.location.href = `/order-success?${params.toString()}`; }, 800);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('The order is taking too long. Please check your connection and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Order placement failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyUpi = () => {
    navigator.clipboard.writeText(vendor.upi_id).then(() => {
      setUpiCopied(true);
      setTimeout(() => setUpiCopied(false), 2000);
    });
  };

  const handleChangeDetails = () => {
    localStorage.removeItem(PROFILE_KEY);
    setFormData({ name: '', phone: '' });
    setStep('info');
  };

  const handleClose = () => onOpenChange(false);

  const renderSchedule = () => (
    <div className="rounded-xl border p-3">
      <p className="mb-2 text-sm font-medium">When do you need it?</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setSchedule({ mode: 'asap', time: '' })}
          data-testid="sched-asap"
          className={`rounded-lg border px-3 py-2 text-sm transition-colors ${schedule.mode === 'asap' ? 'border-primary bg-primary/10 font-semibold' : 'text-muted-foreground hover:bg-accent'}`}
        >
          ⚡ As soon as possible
        </button>
        <button
          type="button"
          onClick={() => setSchedule((s) => ({ ...s, mode: 'scheduled' }))}
          data-testid="sched-scheduled"
          className={`rounded-lg border px-3 py-2 text-sm transition-colors ${schedule.mode === 'scheduled' ? 'border-primary bg-primary/10 font-semibold' : 'text-muted-foreground hover:bg-accent'}`}
        >
          🕒 Schedule a time
        </button>
      </div>
      {schedule.mode === 'scheduled' && (
        <div className="mt-3 space-y-1">
          <Label className="text-xs">Ready by</Label>
          <input
            type="time"
            value={schedule.time}
            onChange={(e) => setSchedule((s) => ({ ...s, time: e.target.value }))}
            data-testid="schedule-time"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
            required
          />
          {schedule.time && (
            <p className="text-xs text-muted-foreground">Requested for today, {to12h(schedule.time)}</p>
          )}
        </div>
      )}
    </div>
  );

  const totalRow = (
    <div className="flex justify-between text-lg font-bold mb-4">
      <span>Total:</span>
      <span data-testid="checkout-total">{currencySymbol}{totalPrice.toFixed(2)}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="checkout-dialog" className="w-[calc(100vw-1.5rem)] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Checkout</DialogTitle>
        </DialogHeader>

        {/* Payment step */}
        {step === 'payment' && (
          <div className="space-y-4">
            <div className="rounded-xl border p-4 bg-amber-50/60 border-amber-200">
              <p className="text-sm font-semibold text-amber-900 mb-3">How would you like to pay?</p>

              {vendor?.payment_qr_url && (
                <div className="flex justify-center mb-4">
                  <img
                    src={vendor.payment_qr_url}
                    alt="Payment QR Code"
                    className="max-w-[180px] rounded-xl border shadow"
                  />
                </div>
              )}

              {vendor?.upi_id && (
                <button
                  type="button"
                  onClick={copyUpi}
                  className="w-full flex items-center justify-between rounded-lg border border-dashed border-amber-400 bg-white px-3 py-2.5 text-sm mb-4 hover:bg-amber-50 transition-colors"
                >
                  <span className="font-mono font-medium">{vendor.upi_id}</span>
                  {upiCopied ? (
                    <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                      <Check className="h-3 w-3" /> Copied!
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-700 text-xs font-semibold">
                      <Copy className="h-3 w-3" /> Tap to copy UPI
                    </span>
                  )}
                </button>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('online')}
                  className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-all text-left ${paymentMethod === 'online' ? 'border-green-500 bg-green-50 text-green-800' : 'border-muted hover:bg-accent text-muted-foreground'}`}
                >
                  💳 Pay Now
                  <p className="text-[10px] font-normal mt-0.5 opacity-70">{hasQrOrUpi ? 'Scan QR & pay online' : 'I will pay online'}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('counter')}
                  className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-all text-left ${paymentMethod === 'counter' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-muted hover:bg-accent text-muted-foreground'}`}
                >
                  🏪 Pay at Counter
                  <p className="text-[10px] font-normal mt-0.5 opacity-70">Pay when you collect</p>
                </button>
              </div>
            </div>

            <div className="border-t pt-4">
              {totalRow}
              <Button
                className="w-full"
                onClick={() => placeOrder(paymentMethod)}
                disabled={loading}
                data-testid="place-order-btn"
              >
                {loading ? 'Placing order…' : paymentMethod === 'online' ? 'Confirm & Pay Online' : 'Confirm Order'}
              </Button>
              <Button
                variant="ghost"
                className="w-full mt-2"
                onClick={() => setStep(loadProfile() ? 'confirm' : 'info')}
                disabled={loading}
              >
                ← Back
              </Button>
            </div>
          </div>
        )}

        {/* Returning customer — one-tap order */}
        {step === 'confirm' && (
          <div className="space-y-5">
            <div className="rounded-xl border p-4">
              <p className="text-sm text-muted-foreground">Ordering as</p>
              <p className="font-semibold leading-tight">{formData.name}</p>
              <p className="text-sm text-muted-foreground">{formData.phone}</p>
            </div>
            {renderSchedule()}
            <div className="border-t pt-4">
              {totalRow}
              <Button className="w-full" onClick={proceedFromConfirm} disabled={loading} data-testid="place-order-btn">
                {loading ? 'Placing order…' : 'Continue to Payment →'}
              </Button>
              <Button variant="ghost" className="w-full mt-2" onClick={handleChangeDetails} disabled={loading} data-testid="switch-account-btn">
                Change name / number
              </Button>
            </div>
          </div>
        )}

        {/* New customer — just name + number */}
        {step === 'info' && (
          <form onSubmit={(e) => { e.preventDefault(); proceedFromInfo(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="checkout-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp number</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g. 9779001200"
                required
                data-testid="checkout-phone-input"
              />
              <p className="text-xs text-muted-foreground">
                We'll send your order confirmation here on WhatsApp.
              </p>
            </div>
            {renderSchedule()}
            <div className="border-t pt-4">
              {totalRow}
              <Button type="submit" className="w-full" disabled={loading} data-testid="place-order-info-btn">
                {loading ? 'Placing order…' : 'Continue to Payment →'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
